import React, { forwardRef, useEffect, useRef, useState } from "react";
import * as PropTypes from "prop-types";
import Grid from "@mui/material/Grid2";
import PencilIcon from "../../../../assets/images/custom/pencil-alt.svg";
import AnalogIcon from "../../../../assets/images/custom/analog_icon.svg";
import DigitalIcon from "../../../../assets/images/custom/digital_icon.svg";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import localizationKeys from "../../../i18n/localizationKeys";
import { useTranslation } from "react-i18next";
import { fetchTrendDataByChannelNos } from "../../../api/channel";
import { TableVirtuoso } from "react-virtuoso";
import { customChannelSelectorStyles } from "../../trendPage/customGraph/virtualScrollTableStyle";
import { getComparator, stableSort } from "../../channelPage/utils/helper";
import {
  VSTableBodyCellStyled,
  VSTableCellStyled,
  VSTableRowStyled,
} from "../../channelPage/utils/vsTableStyled";
import { isAnalogChannel, isDigitalChannel } from "../../../constants/channel/config";
import { RemoveIcon } from "../../trendPage/customGraph/removeIcon";
import { ResetIcon } from "../../alarmPage/resetIcon";
import { DASHBOARD_ITEM_TYPE } from "../constants";
import { getDashboard, postDashboard } from "../../../api/dashboard";
import DeleteIcon from "@mui/icons-material/Delete";
import { CONFIRMATION_MODAL_PROMPTS } from "../../../constants/customDashboard/modalPrompt";
import dayjs from '../../../util/dayjs-init';
import { MAX_CHANNEL_COUNT } from "../../../constants/trendGraph/charts";

const styles = {
  ...customChannelSelectorStyles,
  chartTitle: {
    fontSize: "18px",
    width: "100%",
    position: "relative",
    "& input": {
      padding: "5px 41px 9px",
      minWidth: "50px",
      width: "calc(100%)",
      color: "#ffffff",
      background: "rgba(1, 1, 1, 0)",
      border: 0,
      "&:hover": {
        padding: "4px 40px 8px",
        borderRadius: "4px",
        border: "solid 1px rgba(156, 156, 156, 0.5)",
      },
      "&:focus": {
        padding: "4px 40px 8px",
        borderRadius: "4px",
        border: "solid 1px #9c9c9c",
        backgroundColor: "rgba(44, 58, 72, 0.3)",
      },
      "&:focus-visible": {
        outline: "none",
      },
    },
    "&:hover": {
      "& input": {
        padding: "4px 40px 8px",
        borderRadius: "4px",
        border: "solid 1px rgba(156, 156, 156, 0.5)",
      },
    },
  },
};

export const EditLiveTrendGraph = ({
  vesselId,
  setIsOpenLiveTrendPage,
  selectedDashboard,
  setSelectedDashboard,
  setShowPrompt,
  setPromptContent,
  setDashboards,
  setIsDrawerOpen,
  editingLiveTrendChart,
  setEditingLiveTrendChart,
}) => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [liveTrendTitle, setLiveTrendTitle] = useState(`New Live Trend`);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [fetching, setFetching] = useState(false);
  const liveTrendTitleRef = useRef(null);
  const fetchData = async () => {
    setFetching(true);
    const resp = await fetchTrendDataByChannelNos(vesselId, '', dayjs.utc().add(-1, 'hour'), dayjs.utc(), 0);

    if (resp) {
      const config = resp?.trendData?.config;
      const data = [];

      config.chNo.forEach((ch, idx) => {
        data.push({
          chNo: ch,
          chName: config.chName[idx],
          unit: config.unit[idx],
          chType: config.type[idx],
          sensorType: config.sType[idx],
          status: config.status[idx],
          selected: selectedChannels?.some((chNo) => Number(chNo) === Number(ch)),
        })
      });
      const sortedChannels = stableSort(data, getComparator("desc", "chNo"));

      setChannels(sortedChannels ?? []);
    }
    setFetching(false);
  };

  useEffect(() => {
    if (!editingLiveTrendChart || fetching) return;

    setChannels((prev) =>
      prev.map((p) => {
        p.selected = editingLiveTrendChart.chNos.includes(p.chNo);
        return p;
      })
    ); 

    setSelectedChannels(editingLiveTrendChart.chNos);
    setLiveTrendTitle(editingLiveTrendChart.chartName);
    updateSearchValue("");
  }, [editingLiveTrendChart, fetching]);

  const updateSearchValue = (value) => {
    setSearchValue(value);
    value = value?.toLowerCase();
    if (value) {
      setFilteredChannels(
        channels.filter(
          (ch) => ch.chName?.toLowerCase().includes(value) || ch.chNo.toString().includes(value)
        )
      );
    } else {
      setFilteredChannels(channels);
    }
  };

  const updateSelectedChannels = (chNo, channel, selected) => {
    if (selected) {
      setSelectedChannels([...selectedChannels, chNo]);
      channel.selected = true;
    } else {
      setSelectedChannels([...selectedChannels].filter((ch) => ch !== chNo));
      channel.selected = false;
    }
  };

  const showAll = () => {
    updateSearchValue("");
  };

  const onSave = () => {
    const okHandler = async () => {
      if (editingLiveTrendChart) {
        const items = selectedDashboard.items;
        const chartId = editingLiveTrendChart.id;
        const newItems = items.map((item) => {
          if (item.type !== DASHBOARD_ITEM_TYPE.LIVE || item.id !== chartId) return item;
          return { ...item, chartName: liveTrendTitleRef.current.value, chNos: selectedChannels };
        });
        const newDashboard = { ...selectedDashboard, items: newItems };
        await postDashboard(vesselId, newDashboard);
        const newDashboards = await getDashboard(vesselId);
        setDashboards(newDashboards.Data);
        setSelectedDashboard(
          newDashboards.Data.find((dashboard) => dashboard.id === selectedDashboard.id)
        );
      } else {
        const newItems = selectedDashboard.items.concat({
          id: self.crypto.randomUUID(),
          chartName: liveTrendTitleRef.current.value,
          position: {
            x: 0,
            y: 0, // avoid null in DB, because JSON.stringify(Infinity) is null.
            w: 12,
            h: 9,
          },
          type: DASHBOARD_ITEM_TYPE.LIVE,
          chNos: selectedChannels,
        });
        const newDashboard = { ...selectedDashboard, items: newItems };
        await postDashboard(vesselId, newDashboard);
        const newDashboards = await getDashboard(vesselId);
        setDashboards(newDashboards.Data);
        setSelectedDashboard(
          newDashboards.Data.find((dashboard) => dashboard.id === selectedDashboard.id)
        );
      }
      setShowPrompt(false);
      setIsDrawerOpen(true);
      setEditingLiveTrendChart(null);
      setIsOpenLiveTrendPage(false);
    };
    const cancelHandler = () => {
      setShowPrompt(false);
    };
    setPromptContent({ ...CONFIRMATION_MODAL_PROMPTS.SAVE, okHandler, cancelHandler });
    setShowPrompt(true);
  };

  const onCancel = () => {
    const okHandler = () => {
      setSelectedChannels([]);
      setEditingLiveTrendChart(null);
      setIsOpenLiveTrendPage(false);
      setShowPrompt(false);
      setIsDrawerOpen(true);
    };
    const cancelHandler = () => {
      setShowPrompt(false);
    };
    setPromptContent({ ...CONFIRMATION_MODAL_PROMPTS.CANCEL, okHandler, cancelHandler });
    setShowPrompt(true);
  };

  const onDelete = () => {
    const okHandler = async () => {
      const items = selectedDashboard.items;
      const chartId = editingLiveTrendChart.id;
      const newItems = items.filter(
        (item) => item.type !== DASHBOARD_ITEM_TYPE.LIVE || item.id !== chartId
      );
      const newDashboard = { ...selectedDashboard, items: newItems };
      await postDashboard(vesselId, newDashboard);
      const newDashboards = await getDashboard(vesselId);
      setDashboards(newDashboards.Data);
      setSelectedDashboard(
        newDashboards.Data.find((dashboard) => dashboard.id === selectedDashboard.id)
      );
      setShowPrompt(false);
      setIsDrawerOpen(true);
      setEditingLiveTrendChart(null);
      setIsOpenLiveTrendPage(false);
    };
    const cancelHandler = () => {
      setShowPrompt(false);
    };
    setPromptContent({
      ...CONFIRMATION_MODAL_PROMPTS.DELETE,
      okHandler,
      cancelHandler,
      isDelete: true,
    });
    setShowPrompt(true);
  };

  useEffect(() => {
    if (!vesselId) return;

    fetchData();
  }, [vesselId]);

  useEffect(() => {
    if (searchValue === "") {
      updateSearchValue("");
    }
  }, [channels]);

  const TableHeader = () => (
    <VSTableRowStyled sx={{ ...styles.headerHolder }}>
      <VSTableCellStyled key={`custom-header-1`} width={"15%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomChannelNo)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-2`} width={"40%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomChannelName)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-3`} width={"15%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomUnit)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-4`} width={"20%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomSignalType)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-5`} width={"10%"} sx={{ borderBottom: "none" }} />
    </VSTableRowStyled>
  );

  const TableCustBody = (channel) => {
    const isDisabled = selectedChannels.length >= MAX_CHANNEL_COUNT && !selectedChannels.includes(channel.chNo);
    return (
      <>
        <VSTableBodyCellStyled component="th" scope="row" selected={channel.selected} isDisabled={isDisabled}>
          {channel.chNo}
        </VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="left" selected={channel.selected} isDisabled={isDisabled}>
          {channel.chName}
        </VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="left" selected={channel.selected} isDisabled={isDisabled}>
          {channel.unit}
        </VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="left" selected={channel.selected} isDisabled={isDisabled}>
          {isAnalogChannel(channel?.chType) ? (
            <Box
              sx={{
                borderRadius: "25px",
                border: "1px solid rgba(250, 48, 248, 0.7)",
                padding: "1.5px 0 0.5px 6px",
                width: "72px",
              }}
            >
              <img src={AnalogIcon} style={{ marginBottom: "-4px" }} />
              <span style={{ marginLeft: "1px" }}>Analog</span>
            </Box>
          ) : isDigitalChannel(channel?.chType) ? (
            <Box
              sx={{
                borderRadius: "25px",
                border: "1px solid rgba(132, 235, 128, 0.7)",
                padding: "1.5px 0 0.5px 6px",
                width: "72px",
              }}
            >
              <img src={DigitalIcon} style={{ marginBottom: "-4px" }} />
              <span style={{ marginLeft: "1px" }}>Digital</span>
            </Box>
          ) : (
            <Box
              sx={{
                borderRadius: "25px",
                border: "1px solid rgba(132, 235, 128, 0.7)",
                padding: "1.5px 0 0.5px 6px",
                width: "72px",
              }}
            >
              <img src={DigitalIcon} style={{ marginBottom: "-4px" }} />
              <span style={{ marginLeft: "1px" }}>Other</span>
            </Box>
          )}
        </VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="right" selected={channel.selected} isDisabled={isDisabled}>
          <input
            type="checkbox"
            key={channel.chNo}
            id={channel.chNo}
            value={channel.chNo}
            checked={channel.selected}
            data-ch-type={channel.chType}
            onChange={(event) => {
              updateSelectedChannels(channel.chNo, channel, event.target.checked);
            }}
            disabled={isDisabled}
          />
        </VSTableBodyCellStyled>
      </>
    );
  };
  const VirtuosoTableComponents = {
    Table: (props) => <Table stickyHeader {...props} />,
    TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
    TableRow,
    TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
  };

  const displaySelectedChannel = (chNo) => {
    const channel = channels.find((c) => Number(c.chNo) === Number(chNo));
    return channel ? (
      <Box
        key={chNo}
        sx={{
          ...styles.line,
          border: isAnalogChannel(channel?.chType) ? "1px solid #fa30f8b3" : "1px solid #84eb80b3",
        }}
      >
        {isAnalogChannel(channel?.chType) ? (
          <img src={AnalogIcon} style={{ marginBottom: "-1px", marginRight: "8px" }} />
        ) : (
          <img src={DigitalIcon} style={{ marginBottom: "-1px", marginRight: "8px" }} />
        )}
        <Box sx={styles.lineTitle}>{channel.chName}</Box>
        <Box sx={styles.lineIcon} onClick={() => updateSelectedChannels(chNo, channel, false)}>
          <RemoveIcon />
        </Box>
      </Box>
    ) : <></>;
  };

  return (
    <Grid sx={{ color: "white", paddingTop: "20px", paddingLeft: "12px" }}>
      <Grid sx={styles.titleContainer}>
        <Box sx={styles.chartTitle}>
          <Box sx={styles.titleIcon}>
            <img src={PencilIcon} width={19} height={20} style={{ cursor: "pointer" }} />
          </Box>
          <input defaultValue={liveTrendTitle} size={60} maxLength={31} ref={liveTrendTitleRef} />
        </Box>
      </Grid>
      <Grid container sx={styles.body} spacing={2}>
        <Grid item size={{ xs: 6 }}>
          <Box sx={styles.searchControl}>
            <Box>{t(localizationKeys.CustomSearchChannels)}</Box>
            <Box>
              <Box
                component="input"
                onChange={(evt) => updateSearchValue(evt.target.value)}
                value={searchValue}
                placeholder={t(localizationKeys.CustomSearchChannelsPlaceholder)}
                size="50"
                sx={{ ...styles.searchInput, color: "white" }}
              />
            </Box>
            <Box sx={customChannelSelectorStyles.showAll}>
              {searchValue.length > 0 && (
                <Box onClick={showAll} sx={{ display: "flex", alignItems: "center" }}>
                  <ResetIcon color={"#19B2AB"} />
                  <span>{t(localizationKeys.ResetSearch_lower)}</span>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={styles.tableContainer}>
            {filteredChannels?.length ? (
              <TableContainer sx={styles.table}>
                <TableVirtuoso
                  style={{ width: "100%", height: "100%" }}
                  totalCount={filteredChannels.length}
                  data={filteredChannels}
                  components={VirtuosoTableComponents}
                  fixedHeaderContent={TableHeader}
                  itemContent={(_index, row) => TableCustBody(row)}
                />
              </TableContainer>
            ) : fetching ? (
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress width="100%" height="100%" />
              </Box>
            ) : (
              <Box>
                <Box sx={styles.noMatchFound}>
                  <span>{t(localizationKeys.CustomNoMatchFound)}</span>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item size={{ xs: 6 }}>
          <Box sx={styles.linesContainer}>
            {channels.length > 0 ? (
              <Box sx={styles.lines}>{selectedChannels?.map(displaySelectedChannel)}</Box>
            ) : (
              <Box sx={styles.lines}></Box>
            )}
          </Box>
          <Box sx={styles.controller}>
            <Box sx={styles.controllerLeft}>
              <Button
                onClick={onSave}
                sx={
                  selectedChannels?.length === 0 ? styles.disabledSubmitButton : styles.submitButton
                }
                disabled={selectedChannels?.length === 0}
              >
                {t(localizationKeys.SaveChanges)}
              </Button>
              <Button sx={styles.cancelButton} onClick={onCancel}>
                {t(localizationKeys.CancelButtonText)}
              </Button>
              {editingLiveTrendChart && (
                <Box sx={styles.controllerRight}>
                  <IconButton onClick={onDelete} size="large">
                    <DeleteIcon sx={{ color: "white" }} />
                    <Box component="span" sx={styles.deleteButton}>
                      {t(localizationKeys.CustomDelete)}
                    </Box>
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

EditLiveTrendGraph.propTypes = {
  vesselId: PropTypes.string,
  setIsOpenLiveTrendPage: PropTypes.func,
  selectedDashboard: PropTypes.object,
  setSelectedDashboard: PropTypes.func,
  setShowPrompt: PropTypes.func.isRequired,
  setPromptContent: PropTypes.func.isRequired,
  setDashboards: PropTypes.func,
  setIsDrawerOpen: PropTypes.func,
  editingLiveTrendChart: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOfType([null])]),
  setEditingLiveTrendChart: PropTypes.func,
};
